import React, { memo } from 'react';
import Markdown from 'react-markdown';
import { Box, Card, Typography } from '@mui/material';
import { lighten, useTheme } from '@mui/material/styles';
import { format } from 'date-fns';
import rehypeRaw from 'rehype-raw';
import remarkBreaks from 'remark-breaks';
import { HOUR_MINUTE_SECOND_FORMAT } from 'utils/constants';
import { MessageType, OperatorMessage } from '../operatorChat.types';
import { Keyboard } from './Keyboard';

interface MessageProps {
  onButtonClick: (targetMessage: string, buttonData: string) => void;
  message: OperatorMessage;
  lastMessageRef?: React.Ref<HTMLDivElement>;
  onMenuCommandClick?: (command: string) => void;
}

export const Message = memo(function Message({
  onButtonClick,
  message,
  lastMessageRef,
  onMenuCommandClick,
}: MessageProps) {
  const theme = useTheme();
  const extraStyles: React.CSSProperties = {};
  const isUserMessage = [MessageType.INPUT, MessageType.COMMAND].includes(message.message_type);

  if (isUserMessage) {
    extraStyles['marginLeft'] = '20px';
    extraStyles['backgroundColor'] =
      theme.palette.mode === 'dark' ? theme.palette.brand['forest-green'] : lighten(theme.palette.success.main, 0.88);
  } else {
    extraStyles['marginRight'] = '20px';
    extraStyles['backgroundColor'] = theme.palette.background.paper;
  }

  // Custom link renderer
  const components = {
    a: (props: React.ComponentProps<'a'>) => (
      <a
        {...props}
        onClick={(e) => {
          e.preventDefault();
          if (props.href && props.href.startsWith('command')) {
            const command = props.href.replace('command', '');
            onMenuCommandClick?.(command);
          }
        }}
        style={{ color: theme.palette.primary.main, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
      >
        {props.children}
      </a>
    ),
  };

  return (
    <>
      <Card
        data-testid="message-card"
        elevation={6}
        sx={{ marginBottom: '1em', padding: '0 1em', ...extraStyles }}
        ref={lastMessageRef}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 0.5,
          }}
        >
          <Typography variant="caption" color="text.disabled">
            {isUserMessage && `${message.user_info?.first_name} ${message.user_info?.last_name}`}
          </Typography>
          <Typography variant="caption" color="text.disabled" minHeight={16}>
            {message.timestamp && format(new Date(message.timestamp), HOUR_MINUTE_SECOND_FORMAT)}
          </Typography>
        </Box>
        <Box mt={-2.25}>
          <Markdown remarkPlugins={[remarkBreaks]} rehypePlugins={[rehypeRaw]} components={components}>
            {message.text}
          </Markdown>
        </Box>
      </Card>

      {message.keyboard && (
        <Keyboard keyboard={message.keyboard.inline_keyboard} onButtonClick={onButtonClick} messageId={message.uuid!} />
      )}
    </>
  );
});
